import React, { useState, useEffect, useRef } from 'react';
import Header from '../ui/Header';
import { ScanLine, UserCircle2 } from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';

const ScanView = ({ t, onBack, onScanUser }) => {
  const [scannedData, setScannedData] = useState(null);
  const [error, setError] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const html5QrCodeRef = useRef(null);

  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
        html5QrCodeRef.current.stop().catch(() => {});
      }
    };
  }, []);

  const startScanner = async () => {
    if (isScanning) return;

    try {
      setError(null);
      setIsScanning(true);

      html5QrCodeRef.current = new Html5Qrcode('qr-reader');

      await html5QrCodeRef.current.start(
        { facingMode: 'environment' },
        {
          fps: 30,
          qrbox: { width: 300, height: 300 },
          aspectRatio: 1.0,
          disableFlip: false,
          videoConstraints: {
            facingMode: 'environment',
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        },
        onScanSuccess,
        () => {}
      );
    } catch (err) {
      console.error('QR Scanner error:', err);
      setIsScanning(false);
      if (err.name === 'NotAllowedError' || String(err).includes('Permission')) {
        setError('请允许使用摄像头权限');
      } else {
        setError('摄像头启动失败，请检查权限设置');
      }
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
      try {
        await html5QrCodeRef.current.stop();
      } catch (e) {}
    }
    setIsScanning(false);
  };

  const onScanSuccess = (decodedText) => {
    try {
      const data = JSON.parse(decodedText);

      if (data.type === 'openyc_user' && data.userId) {
        stopScanner();
        setScannedData(data);
        setError(null);
      } else {
        setError('无效的二维码');
      }
    } catch (err) {
      setError('无法识别二维码内容');
    }
  };

  const handleStartChat = () => {
    if (scannedData && onScanUser) {
      onScanUser({
        targetUserId: scannedData.userId,
        targetUserName: scannedData.userName,
        targetAvatar: scannedData.avatarUrl
      });
    }
  };

  const handleRescan = () => {
    setScannedData(null);
    setError(null);
    setTimeout(startScanner, 100);
  };

  const handleBack = () => {
    stopScanner();
    onBack();
  };

  return (
    <div className="flex flex-col h-full animate-in fade-in relative">
      <Header title={t.scan} t={t} onBack={handleBack} />

      <div className="flex-1 flex flex-col items-center justify-center p-4">
        {!scannedData ? (
          <>
            <div className="w-72 h-72 border-2 border-[var(--neon-primary)]/50 rounded-[48px] relative overflow-hidden flex items-center justify-center bg-[var(--neon-primary)]/5 shadow-[0_0_60px_rgba(0,240,255,0.2)]">
              <div
                id="qr-reader"
                className="w-full h-full"
                style={{ borderRadius: '48px', overflow: 'hidden' }}
              ></div>

              {!isScanning && !error && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60">
                  <ScanLine size={64} className="text-[var(--neon-primary)] mb-4" />
                  <button
                    onClick={startScanner}
                    className="px-6 py-3 bg-[var(--neon-primary)] text-black font-bold rounded-full hover:scale-105 transition-transform"
                  >
                    启动摄像头
                  </button>
                </div>
              )}

              {isScanning && (
                <div className="scan-line pointer-events-none absolute inset-0"></div>
              )}
            </div>

            {error && (
              <div className="mt-4 text-center">
                <p className="text-sm text-red-400 mb-3">{error}</p>
                <button
                  onClick={startScanner}
                  className="px-4 py-2 bg-[var(--neon-primary)]/20 text-[var(--neon-primary)] text-sm font-bold rounded-full hover:bg-[var(--neon-primary)]/30 transition-colors"
                >
                  重试
                </button>
              </div>
            )}

            {isScanning && !error && (
              <p className="mt-8 text-sm font-bold text-[var(--neon-primary)] tracking-[0.3em] animate-pulse">
                SCANNING...
              </p>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center animate-in zoom-in">
            <div className="w-20 h-20 rounded-full p-1 bg-gradient-to-tr from-[var(--neon-primary)] to-[var(--neon-secondary)] mb-4">
              {scannedData.avatarUrl ? (
                <img
                  src={scannedData.avatarUrl}
                  alt={scannedData.userName}
                  className="w-full h-full object-cover rounded-full bg-black"
                />
              ) : (
                <UserCircle2 className="w-full h-full text-white/60" />
              )}
            </div>

            <h3 className="text-xl font-bold text-white mb-2">{scannedData.userName}</h3>
            <p className="text-sm text-gray-400 mb-6">扫描成功！</p>

            <div className="flex gap-3">
              <button
                onClick={handleStartChat}
                className="px-6 py-3 bg-[var(--neon-primary)] text-black font-bold rounded-full hover:scale-105 transition-transform"
              >
                开始对话
              </button>
              <button
                onClick={handleRescan}
                className="px-6 py-3 border border-white/20 text-white font-bold rounded-full hover:bg-white/10 transition-colors"
              >
                重新扫描
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        #qr-reader video {
          width: 100% !important;
          height: 100% !important;
          object-fit: cover !important;
          border-radius: 48px !important;
        }
        #qr-reader__scan_region {
          min-height: 100% !important;
        }
        #qr-reader__dashboard {
          display: none !important;
        }
        .scan-line {
          background: linear-gradient(to bottom, transparent, rgba(0,240,255,0.4), transparent);
          height: 4px;
          animation: scan 2s linear infinite;
        }
        @keyframes scan {
          0% { top: 0; }
          100% { top: 100%; }
        }
      `}</style>
    </div>
  );
};

export default ScanView;
